import type { EnumUnion } from '@dresscode-frontend/utility-types';
import type { TYPOGRAPHY_SIZE } from '@standandforce/foundation';
import { Spacing } from '@standandforce/foundation';
import type { ComponentProps } from 'react';
import { Text, TextVariant } from '@/components/Typography';
import { cn } from '@/lib/cn';
import HStack from '../Layout/HStack';
import { IconMap, type IconName } from './iconMap';
type ElementProps = Omit<ComponentProps<'svg'>, keyof IconProps>;
export const IconSize = {
  XXXS: 8,
  XXS: 10,
  XS: 12,
  S: 14,
  DEFAULT: 16,
  M: 18,
  L: 20,
  XL: 24,
  XXL: 32,
  XXXL: 48
} as const;
type IconSizeType = EnumUnion<typeof IconSize>;
const TextColor = {
  MUTED: 'text-muted-foreground',
  PRIMARY: 'text-primary',
  PRIMARY_FILL: 'text-primary-fill',
  PRIMARY_ON_NEUTRAL: 'text-primary-on-neutral-fill',
  NEUTRAL: 'text-neutral-muted-on-fill',
  ERROR: 'text-error-on-neutral-fill',
  DESTRUCTIVE: 'text-destructive',
  SUCCESS: 'text-success-on-neutral-fill'
} as const;
type TextColorType = EnumUnion<typeof TextColor>;
type Variant = {
  iconSize: IconSizeType;
  textSize: keyof typeof TYPOGRAPHY_SIZE;
  gap: string;
};
const variant: Record<'xs' | 'small' | 'default' | 'l', Variant> = {
  xs: {
    iconSize: IconSize.S,
    textSize: 'labelSmall',
    gap: Spacing[4]
  },
  small: {
    iconSize: IconSize.DEFAULT,
    textSize: 'label',
    gap: Spacing[4]
  },
  default: {
    iconSize: IconSize.DEFAULT,
    textSize: 'labelSemiLarge',
    gap: Spacing[6]
  },
  l: {
    iconSize: IconSize.L,
    textSize: 'labelLarge',
    gap: Spacing[8]
  }
} as const;
type BaseComponentProps = {
  /** コンポーネントに適用するクラス名 */
  className?: string;
  /** アイコンと並べるテキスト */
  text?: string;
  /** `true` のとき、アイコンを右側に表示する */
  right?: boolean;
  /** テキストのカラー */
  textColor?: TextColorType;
};
const ColorVariants = {
  active: 'text-active',
  error: 'text-error-on-neutral-fill',
  errorOnMutedFill: 'text-error-on-muted-fill',
  secondaryOnMutedFill: 'text-secondary-on-muted-fill',
  complementaryOnMutedFill: 'text-complementary-on-muted-fill',
  success: 'text-success-on-neutral-fill',
  primary: 'text-primary',
  primaryOnFill: 'text-primary-on-fill',
  primaryOnNeutral: 'text-primary-on-neutral-fill',
  primaryOnNeutralMuted: 'text-primary-muted-on-neutral-fill',
  muted: 'text-neutral-muted-on-fill',
  mutedOnFill: 'text-neutral-muted-on-fill',
  neutral: 'text-neutral-on-fill',
  neutralOnMutedFill: 'text-neutral-on-muted-fill',
  neutralMutedBorder: 'text-neutral-muted-border',
  none: 'text-primary-on-fill',
  warning: 'text-warning-on-muted-fill',
  warningOnNeutral: 'text-warning-on-neutral-fill',
  white: 'text-white'
} as const;
export type ColorVariantType = keyof typeof ColorVariants;
type IconProps = {
  variant?: keyof typeof variant;
  size?: IconSizeType;
  iconName: IconName;
  /**
   * 親コンポーネントで指定したカラーを引き継ぎたい場合にtrueを指定します。
   * 指定したcolorVariantは無視されます。
   * ※ 指定された場合のみ適用する形式にしたい。子コンポーネントとして利用されることが多いため、親コンポーネントで指定したカラーを引き継ぎたい
   */
  inheritColor?: boolean;
  /**
   * アイコンの色のバリエーション
   */
  colorVariant?: ColorVariantType;
};
export type Props = Omit<IconProps & ElementProps, keyof BaseComponentProps> & BaseComponentProps;

/**
 * DressCodeではlucide-iconsを使用してアイコンデザインを表現しています。
 * プロダクトに必要なアイコンのみインポートしているので、新たに追加したい場合は、新たにインポートしたいアイコンをiconMapに追加してください。
 * @doc [lucide-icons](https://lucide.dev/icons/)
 * @example <Icon iconName="CircleDotIcon" />
 */
const Icon: React.FC<Props> = ({
  iconName,
  className,
  size,
  variant: variantKey = 'default',
  text,
  right = false,
  textColor,
  inheritColor = false,
  colorVariant = 'muted'
}) => {
  // 防御的コード: IconMapに存在しない場合はHelpCircleIconをフォールバック
  let IconComponent = IconMap[iconName];
  if (!IconComponent) {
    console.warn(`[Icon] Invalid iconName: "${iconName}", using fallback HelpCircleIcon`);
    IconComponent = IconMap.HelpCircleIcon;
  }
  const existsText = !!text;
  const {
    iconSize,
    textSize,
    gap
  } = variant[variantKey];
  const color = !inheritColor ? ColorVariants[colorVariant] : undefined;
  const textAttachColor = textColor ? textColor : color;
  const svgIcon = <IconComponent className={cn(className, color)} size={size || iconSize} />;
  if (existsText) {
    const textVariant = TextVariant[textSize] ?? TextVariant.p;
    return <HStack className="items-center" style={{
      gap: `${gap}`
    }}>
        {right && <Text variant={textVariant} className={textAttachColor}>
            {text}
          </Text>}
        {svgIcon}
        {!right && <Text variant={textVariant} className={textAttachColor}>
            {text}
          </Text>}
      </HStack>;
  }
  return <>{svgIcon}</>;
};
export default Icon;